import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { formatFileSize } from "~/features/chat/chat.types";
import CustomPopper from "./CustomPopper";
import { VaultFile } from "./FileVaultDrawer";

export interface FileUploadDropzoneProps {
  onFileChange: (files: FileList) => void;
  /** Allowed extensions with leading dot, e.g. [".pdf", ".docx"]. From workflow document_limitations.types. */
  acceptedFileTypes?: string[];
  /** Min files required (from workflow document_limitations.min). */
  minFiles?: number;
  /** Max files allowed (from workflow document_limitations.max). */
  maxFiles?: number;
  fileUploadMaxSize: number;
  error?: string;
  uploadedFiles?: FileList | null;
  className?: string;
  onPickFromVault?: () => void;
  onFileRemove?: (index: number) => void;
  vaultFiles?: VaultFile[];
  onVaultFileRemove?: (index: number) => void;
  onClearAll?: () => void;
  /** Smaller dropzone + shorter helper copy for drawer cards. */
  compact?: boolean;
}

export const FileUploadDropzone = ({
  onFileChange,
  acceptedFileTypes = [".pdf", ".docx"],
  minFiles,
  maxFiles = 5,
  fileUploadMaxSize,
  error = "",
  uploadedFiles,
  className = "",
  onPickFromVault,
  onFileRemove,
  vaultFiles = [],
  onVaultFileRemove,
  onClearAll,
  compact = false,
}: FileUploadDropzoneProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const getTotalFiles = () => {
    const uploaded = uploadedFiles?.length ?? 0;
    const vault = vaultFiles.length;
    return uploaded + vault;
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileChange(files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFileChange(files);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    const pasted: File[] = [];
    items.forEach((item) => {
      if (item.kind !== "file") return;
      const file = item.getAsFile();
      if (!file) return;
      // Clipboard screenshots arrive as "image.png" — synthesize a unique
      // name with timestamp so the user can tell pastes apart in the list.
      const generic = !file.name || /^image\.(png|jpe?g|gif|webp)$/i.test(file.name);
      if (generic) {
        const ext =
          file.type === "image/jpeg"
            ? "jpg"
            : (file.type.split("/")[1] || "bin").toLowerCase();
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        pasted.push(
          new File([file], `pasted-${ts}.${ext}`, { type: file.type }),
        );
      } else {
        pasted.push(file);
      }
    });
    if (pasted.length === 0) return;
    e.preventDefault();
    // The dropzone only reads .length and iterates via Array.from(...) on
    // uploadedFiles, and consumer onFileChange callbacks do the same — so a
    // File[] cast to FileList is structurally compatible (mirrors the
    // HitlFileUploadPanel internal-storage trick documented inline there).
    onFileChange(pasted as unknown as FileList);
  };

  return (
    <div className={className}>
      <input
        type="file"
        id="file-upload"
        multiple
        accept={acceptedFileTypes.join(",")}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onPaste={handlePaste}
        tabIndex={0}
        aria-label="File drop zone — drop, click to browse, or paste"
        data-testid="file-upload-dropzone"
        className={`
          border-2 border-dashed rounded-lg text-center transition-colors
          focus:outline-none focus:ring-2 focus:ring-sunrise focus:ring-offset-1
          ${compact ? "p-3" : "p-8"}
          ${
            isDragging
              ? "border-sunrise bg-orange-50"
              : "border-primary-gray-light-300 bg-white hover:border-primary-gray-light-400"
          }
        `}
      >
        <p className={`${compact ? "text-2xs mb-1" : "text-sm mb-2"} text-primary-gray-light-700`}>
          {compact
            ? "Drop a file, paste, or"
            : "Drag and drop your files here, paste a screenshot (Ctrl+V / Cmd+V), or"}
        </p>
        <div className="flex gap-2 justify-center items-center">
          <label htmlFor="file-upload" className="cursor-pointer">
            <span
              className={`inline-block font-bold rounded-lg bg-sunrise border border-sunrise text-primary-gray-light-800 hover:shadow-[0px_0px_2px_2px_rgba(255,164,0,0.24)] ${
                compact ? "px-2 py-1 text-2xs" : "px-3 py-2 text-sm"
              }`}
            >
              {compact ? "Upload" : "Upload Files"}
            </span>
          </label>
          {onPickFromVault && (
            <>
              <span className="mx-2">or</span>
              <button
                type="button"
                onClick={onPickFromVault}
                className="px-3 py-2 text-sm font-bold rounded-lg bg-sunrise border border-sunrise text-primary-gray-light-800 hover:shadow-[0px_0px_2px_2px_rgba(255,164,0,0.24)]"
              >
                Pick from Vault
              </button>
            </>
          )}
        </div>
        <p className={`${compact ? "text-2xs mt-1" : "text-xs mt-3"} text-primary-gray-light-500`}>
          {acceptedFileTypes.join(", ").toUpperCase().replace(/\./g, "")} files
          only
          {minFiles != null && maxFiles != null
            ? ` • Min ${minFiles} Max ${maxFiles} file(s)`
            : minFiles != null
              ? ` • Min ${minFiles} file(s)`
              : ` • Max ${maxFiles} file(s)`}
          {" • "}
          Up to {formatFileSize(fileUploadMaxSize)} for each file
        </p>
      </div>

      {error && (
        <div className="flex justify-end mt-4">
          <div className="text-sm text-primary-warning-600">{error}</div>
        </div>
      )}

      {uploadedFiles && uploadedFiles.length > 0 && (
        <div className="flex justify-end mt-4">
          <CustomPopper
            id="file-upload-popper"
            placement="bottom"
            title={
              <div className="flex flex-col gap-2">
                {Array.from(uploadedFiles).map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-primary-gray-light-700 truncate max-w-[200px]">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFileRemove?.(index);
                      }}
                      className="text-primary-gray-light-500 hover:text-primary-warning-600 transition-colors flex-shrink-0"
                      aria-label={`Remove ${file.name}`}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                ))}
              </div>
            }
            isOnModal={true}
          >
            <span className="text-base text-[#969292] cursor-pointer hover:underline">
              ({uploadedFiles.length}) File(s) Selected
            </span>
          </CustomPopper>
        </div>
      )}

      {vaultFiles.length > 0 && (
        <div className="flex justify-end mt-4">
          <CustomPopper
            id="vault-files-popper"
            placement="bottom"
            title={
              <div className="flex flex-col gap-2">
                {vaultFiles.map((vaultFile, index) => (
                  <div
                    key={`${vaultFile.id}-${index}`}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-primary-gray-light-700 truncate max-w-[200px]">
                      {vaultFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onVaultFileRemove?.(index);
                      }}
                      className="text-primary-gray-light-500 hover:text-primary-warning-600 transition-colors flex-shrink-0"
                      aria-label={`Remove ${vaultFile.name}`}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                ))}
              </div>
            }
            isOnModal={true}
          >
            <span className="text-base text-[#969292] cursor-pointer hover:underline">
              ({vaultFiles.length}) File(s) Selected from Vault
            </span>
          </CustomPopper>
        </div>
      )}

      {getTotalFiles() > 0 && (
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={onClearAll}
            className="px-3 py-2 text-sm font-bold rounded-lg bg-sunrise border border-sunrise text-primary-gray-light-800 hover:shadow-[0px_0px_2px_2px_rgba(255,164,0,0.24)]"
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
};
